import React from 'react';
import type { FreimeldungPublicItem, FreimeldungPublicPlan } from '../../../services/freimeldungPublicService';
import { HINWEISKASTEN, KNOPF_PRIMAER, KNOPF_SEKUNDAER } from '../../sifa/public/teilnahmeSeiteStil';
import {
  OHNE_FIRMA,
  fristText,
  istBereitsGemeldet,
  nummerText,
  planAnsichtenFuer,
  planKnopfText,
  type FirmenKnopf,
  type FreimeldungTexte,
  type MeldeArt,
  type Sprache,
} from './freimeldungLogic';

/**
 * Die offenen Maengel des Berichts als Karten — eine Spalte, grosse Flaechen:
 * die Seite wird am Bauzaun mit dem Daumen bedient.
 *
 * Gezeigt wird nur, was in der oeffentlichen RPC steht (Nummer, Titel, Ort,
 * Zustaendigkeit, Frist, erstes Foto, Nadeln auf freigegebenen Plaenen).
 * Feststellung, Vorschrift und Kontaktdaten bleiben draussen.
 *
 * Oben die Schnellauswahl „Ihre Firma" (nur bei mindestens zwei Knoepfen):
 * wer den QR-Code scannt, sieht mit einem Tipp nur seine eigenen Maengel.
 * `items` ist schon gefiltert, `gesamt` die Zahl aller offenen Maengel.
 */
export const FreimeldungListe: React.FC<{
  items: FreimeldungPublicItem[];
  gesamt: number;
  plaene: FreimeldungPublicPlan[];
  texte: FreimeldungTexte;
  sprache: Sprache;
  firmenKnoepfe: FirmenKnopf[];
  firmaFilter: string | null;
  onFirmaFilter: (wert: string | null) => void;
  onWaehlen: (id: string, art: MeldeArt) => void;
  onPlan: (id: string) => void;
}> = ({ items, gesamt, plaene, texte, sprache, firmenKnoepfe, firmaFilter, onFirmaFilter, onWaehlen, onPlan }) => {
  if (gesamt === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-slate-900">{texte.listeUeberschrift}</h2>
        <div className={HINWEISKASTEN}>{texte.leereListe}</div>
      </div>
    );
  }

  const knopf = (wert: string | null, beschriftung: string) => {
    const aktiv = firmaFilter === wert;
    return (
      <button
        key={wert ?? '__alle__'}
        type="button"
        onClick={() => onFirmaFilter(wert)}
        aria-pressed={aktiv}
        className={`min-h-11 px-3.5 py-2 rounded-full border text-sm font-semibold transition-colors ${aktiv ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}
      >
        {beschriftung}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-slate-900">{texte.listeUeberschrift}</h2>
        <p className="text-slate-600">{texte.listeHinweis}</p>
      </div>

      {firmenKnoepfe.length > 0 && (
        <div className="space-y-2" role="group" aria-label={texte.filterTitel}>
          <p className="text-sm font-semibold text-slate-700">{texte.filterTitel}</p>
          <div className="flex flex-wrap gap-2">
            {knopf(null, texte.filterAlle(gesamt))}
            {firmenKnoepfe.map((k) =>
              knopf(k.wert, `${k.wert === OHNE_FIRMA ? texte.filterOhne : k.wert} (${k.anzahl})`),
            )}
          </div>
        </div>
      )}

      {items.length === 0 && <div className={HINWEISKASTEN}>{texte.leereListeFirma}</div>}

      <ul className="space-y-4 list-none p-0 m-0">
        {items.map((item) => {
          const nummer = nummerText(item);
          const gemeldet = istBereitsGemeldet(item);
          const planKnopf = planKnopfText(planAnsichtenFuer(item, plaene).length, sprache);
          return (
            <li key={item.id} className="border border-slate-200 rounded-2xl p-4 bg-white space-y-3">
              <div className="flex items-start gap-3">
                {item.fotoUrl && (
                  <img
                    src={item.fotoUrl}
                    alt=""
                    loading="lazy"
                    className="w-16 h-16 rounded-xl object-cover border border-slate-200 shrink-0 bg-slate-100"
                  />
                )}
                <div className="min-w-0 space-y-1.5 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {nummer && (
                      <span className="px-2 py-0.5 rounded-lg bg-slate-800 text-white text-sm font-bold">{nummer}</span>
                    )}
                    {item.immediateAction && (
                      <span className="px-2 py-0.5 rounded-lg bg-red-100 text-red-700 text-sm font-semibold">
                        {texte.sofortmassnahme}
                      </span>
                    )}
                    {gemeldet && (
                      <span className="px-2 py-0.5 rounded-lg bg-amber-100 text-amber-800 text-sm font-semibold">
                        {texte.bereitsGemeldet(item.offeneMeldungen)}
                      </span>
                    )}
                  </div>
                  <p className="font-semibold text-slate-900 break-words">{item.title}</p>
                  <dl className="text-sm text-slate-600 space-y-0.5">
                    {item.location && (
                      <div className="flex gap-1.5">
                        <dt className="text-slate-500 shrink-0">{texte.ort}:</dt>
                        <dd className="break-words">{item.location}</dd>
                      </div>
                    )}
                    {item.responsiblePerson && (
                      <div className="flex gap-1.5">
                        <dt className="text-slate-500 shrink-0">{texte.firmaFeld}:</dt>
                        <dd className="break-words">{item.responsiblePerson}</dd>
                      </div>
                    )}
                    <div className="flex gap-1.5">
                      <dt className="text-slate-500 shrink-0">{texte.frist}:</dt>
                      <dd>{fristText(item, sprache)}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button type="button" onClick={() => onWaehlen(item.id, 'behoben')} className={KNOPF_PRIMAER}>
                  {texte.knopfMelden}
                </button>
                <button type="button" onClick={() => onWaehlen(item.id, 'zurueckgewiesen')} className={KNOPF_SEKUNDAER}>
                  {texte.knopfZurueckweisen}
                </button>
                {planKnopf && (
                  <button type="button" onClick={() => onPlan(item.id)} className={KNOPF_SEKUNDAER}>
                    {planKnopf}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className={HINWEISKASTEN}>{texte.hinweisPruefung}</div>
    </div>
  );
};
